import React, { useState, useMemo } from 'react'
import { Dropdown, Menu, Popconfirm, Tooltip } from 'antd'
import MoreOutlined from '@ant-design/icons/MoreOutlined'
import CheckOutlined from '@ant-design/icons/CheckOutlined'
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled'
import classNames from 'classnames'
import ResizeObserver from 'rc-resize-observer'
import styles from './index.less'

import { isNumber } from '@/utils/utils'
import getTextWidth from '@/utils/getTextWidth'

import getValidQuickValues from '../../helper/getValidQuickValues'
import getQuickSearchApplyValues from '../../helper/getQuickSearchApplyValues'

// 获取提示
const getTooltipTitle = ({ quickItem, fieldsGroup, sortFields }) => {
    const { validQueryFields, validSortFields } = getValidQuickValues({ quickItem, fieldsGroup, sortFields })

    return (
        <div className={styles.tooltip}>
            <div>{quickItem.selectName}：</div>
            <div className={styles.tooltipContent}>
                {validQueryFields.map(item => {
                    return (
                        <div key={item.groupKey || item.groupTitle}>
                            {
                                item.groupTitle ? <div className={styles.title}>{item.groupTitle}：</div> : null
                            }
                            {item.fields.map(item => {
                                return <div key={item.name}>{item.displayValue}</div>
                            })}
                        </div>
                    )
                })}

                {validSortFields ? (
                    <div>
                        <div className={styles.title}>排序：</div>
                        <div>{validSortFields.displayValue}</div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

// 获取快捷查询展示列表 padding + border + margin
const ITEM_WITHOUT_LETTER_WIDTH = 16 * 2 + 1 * 2 + 4 * 2

// 更多触发器的宽度
const MORE_TRIGGER_WIDTH = 39

// 按扭区的宽度
const SEARCH_AREA_WIDTH = 0

// 保存快捷查询的宽度
const SAVE_QUICK_SEARCH_BTN_WIDTH = 166

// 排序快捷查询的宽度
const SORT_QUICK_SEARCH_BTN_WIDTH = 102

// 获取项的宽度
const getItemWidth = item => {
    if (typeof item.width === 'number') {
        return item.width
    }

    const textWidth = getTextWidth({ text: item.selectName, font: '13px' }) + ITEM_WITHOUT_LETTER_WIDTH

    if (item.isPre) {
        if (isNumber(item.count)) {
            const countWidth = getTextWidth({ text: item.count, font: 'bold 13px' }) + 5

            return textWidth + countWidth
        }
    }

    return textWidth
}

const MAX_LINE_NUM = 1

// 获取展示数据，超出容器宽度的标签需要通过更多气泡展示
const getQuickSearchDisplayData = ({
    containerWidth,
    quickSearchPreOptions = [],
    quickSearchOptions,
    quickSearchSortable
}) => {
    const preList = quickSearchPreOptions.map((v, k) => ({
        selectName: v.selectName,
        count: v.count,
        index: k,
        isPre: true, // 预设类型
        isLast: k === quickSearchPreOptions.length - 1 // 最后一个
    }))

    const list = quickSearchOptions.map((v, k) => ({
        selectName: v.selectName,
        index: k
    }))

    const saveBtn = {
        isSaveBtn: true,
        width: SAVE_QUICK_SEARCH_BTN_WIDTH,
        visible: true
    }

    const sortBtn = {
        isSortBtn: true,
        width: SORT_QUICK_SEARCH_BTN_WIDTH,
        visible: quickSearchSortable && list?.length
    }

    const totalList = preList.concat([saveBtn, sortBtn].filter(v => v.visible)).concat(list)

    const { expandList, collapsedList } = totalList.reduce(
        (t, c) => {
            const itemWidth = getItemWidth(c)

            t.sumWidth += itemWidth
            t.perLineSumWidth += itemWidth

            // 每行累加的宽度大于容器的宽度
            if (t.perLineSumWidth > containerWidth && t.line < MAX_LINE_NUM) {
                // 每行空的宽度
                const spaceWidth = containerWidth - (t.perLineSumWidth - itemWidth)

                t.sumWidth += spaceWidth
                t.perLineSumWidth = itemWidth
                t.line += 1
            }

            // 非预设项
            if (!c.isPre && !c.isSaveBtn && !c.isSortBtn) {
                const option = quickSearchOptions[c.index]

                if (t.sumWidth + MORE_TRIGGER_WIDTH + 10 <= containerWidth * MAX_LINE_NUM - SEARCH_AREA_WIDTH) {
                    t.expandList.push(option)
                } else {
                    t.collapsedList.push(option)
                }
            }

            return t
        },
        { sumWidth: 0, expandList: [], collapsedList: [], perLineSumWidth: 0, line: 1 }
    )

    return {
        expandList,
        collapsedList
    }
}

const DeleteAction = ({ className, onConfirm, ...restProps }) => {
    return (
        <Popconfirm
            title={<div style={{ width: 100 }}>是否删除?</div>}
            okText="是"
            cancelText="否"
            onConfirm={onConfirm}
        >
            <div
                className={classNames(styles.delete, className)}
                onClick={e => {
                    e.stopPropagation()
                }}
                {...restProps}
            >
                <CloseCircleFilled />
            </div>
        </Popconfirm>
    )
}

const MENU_STYLES = {
    maxHeight: 300,
    overflowY: 'auto'
}

export default ({
    quickSearchSortable,
    quickSearchOptions,
    quickSearchSelected,
    fieldsGroup,
    sortFields,
    children,
    searchArea,
    onSelectQuickSearch,
    onDeleteQuickSearch
}) => {
    const [containerWidth, setContainerWidth] = useState(0)

    const displayData = useMemo(() => {
        return getQuickSearchDisplayData({
            containerWidth,
            quickSearchPreOptions: [],
            quickSearchOptions,
            quickSearchSortable
        })
    }, [containerWidth, quickSearchOptions, quickSearchSortable])

    const checkIsActive = target => {
        return target.key === quickSearchSelected
    }

    const handleContainerResize = ({ width }) => {
        setContainerWidth(width)
    }

    const toggleApplyItem = (item, isActive) => {
        if (isActive) {
            onSelectQuickSearch(getQuickSearchApplyValues({ quickItem: null, fieldsGroup, sortFields }))
        } else {
            onSelectQuickSearch(getQuickSearchApplyValues({ quickItem: item, fieldsGroup, sortFields }))
        }
    }

    const menu = (
        <Menu style={MENU_STYLES}>
            {displayData.collapsedList.map((item, index) => {
                const isActive = checkIsActive(item)

                return (
                    <Menu.Item key={item.key} style={{ padding: 0 }}>
                        <Tooltip
                            overlay={null}
                            placement="left"
                            overlayClassName={styles.tooltipOverlay}
                            title={getTooltipTitle({ quickItem: item, fieldsGroup, sortFields })}
                        >
                            <div
                                className={classNames(styles.itemWrap, {
                                    [styles.isActive]: isActive
                                })}
                            >
                                <div
                                    className={classNames(styles.menuItem, {
                                        [styles.isActive]: isActive
                                    })}
                                    onClick={() => toggleApplyItem(item, isActive)}
                                >
                                    <CheckOutlined className={styles.gou} />
                                    <div>{item.selectName}</div>
                                </div>
                                <DeleteAction
                                    className={styles.menuDelete}
                                    onConfirm={() => onDeleteQuickSearch(item, index)}
                                />
                            </div>
                        </Tooltip>
                    </Menu.Item>
                )
            })}
        </Menu>
    )

    return (
        <ResizeObserver onResize={handleContainerResize}>
            <div className={styles.container}>
                {children}

                {displayData?.expandList?.length === 0 ? (
                    <div className={styles.tip} onClick={e => e.preventDefault()}>
                        <img
                            className={styles.icon}
                            src="https://assets.sursung.com/activity/20240117/E9BD3020CF08472E8E948DDD9640CB3C1705458710010.svg"
                        />
                        将筛选项自定义保存，查询更便捷
                    </div>
                ) : null}

                {displayData.expandList.map((item, index) => {
                    const isActive = checkIsActive(item)

                    return (
                        <Tooltip
                            key={item.key}
                            overlay={null}
                            autoAdjustOverflow={false}
                            placement="bottom"
                            overlayClassName={styles.tooltipOverlay}
                            title={getTooltipTitle({ quickItem: item, fieldsGroup, sortFields })}
                        >
                            <div
                                className={classNames(styles.itemWrap, {
                                    [styles.isActive]: isActive
                                })}
                            >
                                <div
                                    className={classNames(styles.item, {
                                        [styles.isActive]: isActive
                                    })}
                                    onClick={() => {
                                        toggleApplyItem(item, isActive)
                                    }}
                                >
                                    {item.selectName}
                                </div>

                                <DeleteAction onConfirm={() => onDeleteQuickSearch(item, index)} />
                            </div>
                        </Tooltip>
                    )
                })}

                {displayData?.collapsedList?.length ? (
                    <Dropdown overlay={menu} trigger={['click']}>
                        <span onClick={e => e.preventDefault()}>
                            <div className={styles.more}>
                                <MoreOutlined style={{ fontSize: '18px' }} />
                            </div>
                        </span>
                    </Dropdown>
                ) : null}

                {searchArea ? (
                    <>
                        <div style={{ width: `${SEARCH_AREA_WIDTH}px`, height: '36px' }} />
                        <div className={styles.searchArea}>{searchArea}</div>
                    </>
                ) : null}
            </div>
        </ResizeObserver>
    )
}
